import React, { useCallback, useEffect, useLayoutEffect, forwardRef, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sanitizeDomProps } from '../inc/domProps';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Loader } from '../inc/Loader';
import { scroller } from '../inc/Scroller';
import { runtime } from '../inc/Runtime';
import AppLink from './AppLink';
import Button from './Button';

const MENU_COPY = {
    fr: {
        ariaLabel: 'Menu principal',
        menuLabel: 'Menu',
        closeLabel: 'Fermer',
        languageLabel: 'En',
        languagePath: '/en/',
        socialLabel: 'Médias sociaux',
        phoneLabel: 'Téléphone',
        links: [
            {
                text: 'À propos',
                to: '#h__about'
            },
            {
                text: 'Événementiel',
                to: '#h__services-events'
            },
            {
                text: 'Vente & installation',
                to: '#h__services-installation'
            },
            {
                text: 'Services aux écoles',
                to: '#h__school-service'
            },
            {
                text: 'Témoignages',
                to: '#h__testimonials'
            },
            {
                text: 'Nous joindre',
                to: '#h__contact'
            }
        ]
    },
    en: {
        ariaLabel: 'Main menu',
        menuLabel: 'Menu',
        closeLabel: 'Close',
        languageLabel: 'Fr',
        languagePath: '/',
        socialLabel: 'Social media',
        phoneLabel: 'Phone',
        links: [
            {
                text: 'About',
                to: '#h__about'
            },
            {
                text: 'Events',
                to: '#h__services-events'
            },
            {
                text: 'Sales & installation',
                to: '#h__services-installation'
            },
            {
                text: 'School services',
                to: '#h__school-service'
            },
            {
                text: 'Testimonials',
                to: '#h__testimonials'
            },
            {
                text: 'Contact us',
                to: '#h__contact'
            }
        ]
    }
};

const SOCIAL_LINKS = [
    {
        text: 'Facebook',
        to: '#',
        target: '_blank'
    },
    {
        text: 'Instagram',
        to: '#',
        target: '_blank'
    }
];

const DEFAULT_CONTACT_INFO = {
    phone: '514 942-4435',
    phoneUrl: 'tel:5149424435'
};

const cleanText = value => typeof value === 'string' ? value.trim() : '';

const normalizeLangCode = lang => String(lang || '').trim().split(/[-_]/)[0].toLowerCase();

const getCurrentLang = route => {
    const htmlLang = typeof document !== 'undefined'
        ? document.documentElement?.lang
        : '';
    const lang = [
        route?.lang,
        route?.language,
        route?.locale,
        htmlLang,
        runtime.site?.language,
        runtime.bootstrap?.site?.language,
        runtime.route?.lang,
        runtime.route?.language,
        runtime.route?.locale
    ].map(normalizeLangCode).find(Boolean);

    return lang || 'fr';
};

const getMenuCopy = route => MENU_COPY[getCurrentLang(route)] || MENU_COPY.fr;

const phoneHrefFromText = phone => {
    const normalized = cleanText(phone).replace(/[^\d+]/g, '');

    return normalized ? `tel:${normalized}` : '#';
};

const normalizeSocialLink = link => {
    if(!link || typeof link !== 'object' || Array.isArray(link)){
        return null;
    }

    const text = cleanText(link.text || link.title || link.label);
    const to = cleanText(link.url || link.href || link.to);

    if(!text || !to){
        return null;
    }

    return {
        text,
        to,
        target: '_blank'
    };
};

const THEME_SOCIAL_LINKS = Array.isArray(runtime.theme?.socialLinks)
    ? runtime.theme.socialLinks.map(normalizeSocialLink).filter(Boolean)
    : [];
const MENU_SOCIAL_LINKS = THEME_SOCIAL_LINKS.length ? THEME_SOCIAL_LINKS : SOCIAL_LINKS;
const THEME_CONTACT_INFO = runtime.theme?.contactInfo && typeof runtime.theme.contactInfo === 'object'
    ? runtime.theme.contactInfo
    : {};
const MENU_CONTACT_INFO = {
    phone: cleanText(THEME_CONTACT_INFO.phone) || DEFAULT_CONTACT_INFO.phone,
    phoneUrl: cleanText(THEME_CONTACT_INFO.phoneUrl) || phoneHrefFromText(THEME_CONTACT_INFO.phone || DEFAULT_CONTACT_INFO.phone)
};

const MENU_BUTTON_LABEL_ANIMATION = {
    fadeOutDuration: .22,
    fadeInDuration: .3,
    widthDuration: .38,
    offset: 5
};

const MENU_MOBILE_QUERY = '(max-width: 768px)';
const MENU_DESKTOP_PAGE_OPACITY = .65;
const MENU_PAGE_FADE_DURATION = .35;

const getOpenPageOpacity = () => {
    if(typeof window === 'undefined'){
        return MENU_DESKTOP_PAGE_OPACITY;
    }

    return window.matchMedia(MENU_MOBILE_QUERY).matches ? 0 : MENU_DESKTOP_PAGE_OPACITY;
};

const Header = forwardRef(function Header({
    show = false,
    className = null,
    mountId = 'app-header',
    route = null,
    ...props
}, ref){
    
    const localRef = useRef(null);
    const headerRef = ref || localRef;
    const content = getMenuCopy(route);
    const menuLinks = content.links;

    const plyWoodRef = useRef(null);
    const languageButtonRef = useRef(null);
    const menuButtonRef = useRef(null);
    const menuButtonTextRef = useRef(null);
    const menuIconRef = useRef(null);
    const menuPanelRef = useRef(null);
    const menuRevealItemsRef = useRef([]);
    const menuTimelineRef = useRef(null);
    const menuOpenRef = useRef(false);
    const menuScrollLockedRef = useRef(false);
    const pendingMenuButtonWidthRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuButtonLabel, setMenuButtonLabel] = useState(content.menuLabel);

    const setMenuRevealItem = index => element => {
        menuRevealItemsRef.current[index] = element;
    };

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const toggleMenu = useCallback(() => {
        setIsMenuOpen(isOpen => !isOpen);
    }, []);

    const handleMenuLinkClick = useCallback((event) => {
        const target = event.currentTarget?.getAttribute('target');

        if(
            event.defaultPrevented
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
            || String(target || '').toLowerCase() === '_blank'
        ){
            return;
        }

        closeMenu();
    }, [closeMenu]);

    const syncMenuPanelLayout = useCallback(() => {
        const menuButton = menuButtonRef.current;
        const menuPanel = menuPanelRef.current;

        if(!menuButton || !menuPanel){
            return;
        }

        const buttonRect = menuButton.getBoundingClientRect();
        const isSmallScreen = window.innerWidth <= 640;
        const edgeOffset = isSmallScreen ? 12 : 20;
        const topOffset = isSmallScreen ? 8 : 15;
        const innerOffset = isSmallScreen ? 42 : 55;
        const panelTop = Math.max(edgeOffset, buttonRect.top - topOffset);
        const panelRight = Math.max(edgeOffset, window.innerWidth - buttonRect.right - 10);
        const panelPaddingTop = Math.max(isSmallScreen ? 78 : 90, buttonRect.bottom - panelTop + innerOffset);

        gsap.set(menuPanel, {
            '--menu-panel-top': `${panelTop}px`,
            '--menu-panel-right': `${panelRight}px`,
            '--menu-panel-padding-top': `${panelPaddingTop}px`
        });
        
    }, []);

    if(!show){
        return null;
    }

    const mountNode = typeof document !== 'undefined'
        ? (document.getElementById(mountId) || document.body)
        : null;

    if(!mountNode){
        return null;
    }

    const headerClassName = ['site-header', className, isMenuOpen ? 'is-menu-open' : ''].filter(Boolean).join(' ');
    const tagProps = {
        className: headerClassName,
        ...sanitizeDomProps(props)
    };

    useEffect(() => {

        let lastDirection = -1;

        let headerAnim = gsap.to(headerRef.current, .2, {
            opacity: 0
        });

        headerAnim.paused(true);

        let st = ScrollTrigger.create({
            trigger: '#pageContent',
            start: 'top top',
            end: 'bottom bottom',
            onUpdate: (self) => {

                if(menuOpenRef.current){
                    headerAnim.reverse();
                    return;
                }

                if(self.direction === lastDirection) return;

                lastDirection = self.direction;

                headerAnim.play();
                headerAnim.reversed(lastDirection !== 1);



            }
        });


        return () => {

            headerAnim?.revert();
            headerAnim = null;

            st?.kill();
            st = null;

        }

    }, []);

    useEffect(() => {
        const menuPanel = menuPanelRef.current;
        const menuIcon = menuIconRef.current;
        const languageButton = languageButtonRef.current;
        const revealItems = menuRevealItemsRef.current.filter(Boolean);

        if(!menuPanel){
            return undefined;
        }

        syncMenuPanelLayout();

        gsap.set(menuPanel, {
            autoAlpha: 0,
            scale: .94,
            y: -10,
            pointerEvents: 'none',
            transformOrigin: 'right top'
        });

        gsap.set(revealItems, {
            autoAlpha: 0,
            y: 18
        });

        gsap.set(menuIcon, {
            rotate: 0,
            transformOrigin: '50% 50%'
        });

        const tl = gsap.timeline({
            paused: true,
            defaults: {
                overwrite: 'auto'
            },
            onReverseComplete: () => {
                gsap.set(menuPanel, {
                    pointerEvents: 'none'
                });
            }
        });

        tl
        .set(menuPanel, {
            pointerEvents: 'auto'
        })
        .to(menuPanel, .45, {
            autoAlpha: 1,
            scale: 1,
            y: 0,
            ease: 'power3.out'
        })
        .to(languageButton, .22, {
            autoAlpha: 0,
            x: 8,
            ease: 'power2.out'
        }, 0)
        .to(menuIcon, .34, {
            rotate: 45,
            ease: 'power3.out'
        }, 0)
        .to(revealItems, .38, {
            autoAlpha: 1,
            y: 0,
            stagger: .035,
            ease: 'power3.out'
        }, '-=.24');

        menuTimelineRef.current = tl;

        return () => {
            menuTimelineRef.current = null;
            tl.kill();
        };
    }, [syncMenuPanelLayout]);

    useEffect(() => {
        menuOpenRef.current = isMenuOpen;
        const pageContent = document.getElementById('pageContent');

        if(pageContent){
            gsap.killTweensOf(pageContent);
            gsap.to(pageContent, {
                opacity: isMenuOpen ? getOpenPageOpacity() : 1,
                duration: MENU_PAGE_FADE_DURATION,
                ease: 'power2.out',
                overwrite: true,
                onStart: () => {
                    if(isMenuOpen){
                        gsap.set(pageContent, {
                            pointerEvents: 'none'
                        });
                    }
                },
                onComplete: () => {
                    if(!isMenuOpen){
                        gsap.set(pageContent, {
                            clearProps: 'opacity,pointerEvents'
                        });
                    }
                }
            });
        }

        if(isMenuOpen){
            syncMenuPanelLayout();

            if(!menuScrollLockedRef.current){
                scroller.lock();
                menuScrollLockedRef.current = true;
            }

            gsap.set(headerRef.current, {
                opacity: 1
            });

            menuTimelineRef.current?.play();
        } else {
            if(menuScrollLockedRef.current){
                scroller.unlock();
                menuScrollLockedRef.current = false;
            }

            menuTimelineRef.current?.reverse();
        }
    }, [headerRef, isMenuOpen, syncMenuPanelLayout]);

    useEffect(() => {
        return () => {
            const pageContent = document.getElementById('pageContent');

            if(pageContent){
                gsap.killTweensOf(pageContent);
                gsap.set(pageContent, {
                    clearProps: 'opacity,pointerEvents'
                });
            }
        };
    }, []);

    useEffect(() => {
        if(!isMenuOpen){
            return undefined;
        }

        const onResize = () => {
            syncMenuPanelLayout();

            const pageContent = document.getElementById('pageContent');

            if(pageContent){
                gsap.to(pageContent, {
                    opacity: getOpenPageOpacity(),
                    duration: .2,
                    ease: 'power2.out',
                    overwrite: true
                });
            }
        };

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [isMenuOpen, syncMenuPanelLayout]);

    useEffect(() => () => {
        if(menuScrollLockedRef.current){
            scroller.unlock();
            menuScrollLockedRef.current = false;
        }
    }, []);

    useEffect(() => {
        const nextLabel = isMenuOpen ? content.closeLabel : content.menuLabel;
        const menuButton = menuButtonRef.current;
        const menuButtonText = menuButtonTextRef.current;

        if(nextLabel === menuButtonLabel){
            if(menuButtonText){
                gsap.to(menuButtonText, .18, {
                    autoAlpha: 1,
                    y: 0,
                    ease: 'sine.out',
                    overwrite: true
                });
            }

            return undefined;
        }

        if(!menuButton || !menuButtonText){
            setMenuButtonLabel(nextLabel);
            return undefined;
        }

        gsap.killTweensOf([menuButton, menuButtonText]);
        pendingMenuButtonWidthRef.current = menuButton.offsetWidth;

        gsap.set(menuButton, {
            width: pendingMenuButtonWidthRef.current
        });

        const tween = gsap.to(menuButtonText, MENU_BUTTON_LABEL_ANIMATION.fadeOutDuration, {
            autoAlpha: 0,
            y: -MENU_BUTTON_LABEL_ANIMATION.offset,
            ease: 'sine.inOut',
            onComplete: () => {
                setMenuButtonLabel(nextLabel);
            }
        });

        return () => {
            tween.kill();
        };
    }, [content.closeLabel, content.menuLabel, isMenuOpen, menuButtonLabel]);

    useLayoutEffect(() => {
        const fromWidth = pendingMenuButtonWidthRef.current;
        const menuButton = menuButtonRef.current;
        const menuButtonText = menuButtonTextRef.current;

        if(!menuButton || !menuButtonText){
            return undefined;
        }

        if(fromWidth === null){
            gsap.set(menuButtonText, {
                autoAlpha: 1,
                y: 0
            });

            return undefined;
        }

        pendingMenuButtonWidthRef.current = null;
        gsap.killTweensOf([menuButton, menuButtonText]);
        gsap.set(menuButton, {
            width: 'auto'
        });

        const toWidth = menuButton.offsetWidth;

        gsap.set(menuButton, {
            width: fromWidth
        });

        gsap.set(menuButtonText, {
            autoAlpha: 0,
            y: MENU_BUTTON_LABEL_ANIMATION.offset
        });

        const widthTween = gsap.to(menuButton, MENU_BUTTON_LABEL_ANIMATION.widthDuration, {
            width: toWidth,
            ease: 'power2.inOut',
            onComplete: () => {
                gsap.set(menuButton, {
                    width: 'auto'
                });
            }
        });

        const textTween = gsap.to(menuButtonText, MENU_BUTTON_LABEL_ANIMATION.fadeInDuration, {
            autoAlpha: 1,
            y: 0,
            delay: .04,
            ease: 'sine.out'
        });

        return () => {
            widthTween.kill();
            textTween.kill();
        };
    }, [menuButtonLabel]);

    useEffect(() => {
        if(!isMenuOpen){
            return undefined;
        }

        const onPointerDown = event => {
            const target = event.target;

            if(
                !(target instanceof Node)
                || menuPanelRef.current?.contains(target)
                || menuButtonRef.current?.contains(target)
            ){
                return;
            }

            closeMenu();
        };

        const onKeyDown = event => {
            if(event.key === 'Escape'){
                closeMenu();
            }
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [closeMenu, isMenuOpen]);

    useEffect(() => {
        let active = true;

        const tl = gsap.timeline();


        tl
        .to(plyWoodRef.current, .8, {
            y: 0,
            ease: 'power4'
        })
        .paused(true);

        Loader.whenInitialLoadDone().then(() => {
            if(active){
                tl.play();
            }
        });

        return () => {
            active = false;
            tl.kill();
        };

    }, []);

    return createPortal(
        <header ref={headerRef} {...tagProps}>
            <div ref={plyWoodRef} className="plywood">
                <div className="container">
                    <AppLink to="#h__hero">
                        <svg xmlns="http://www.w3.org/2000/svg" width="45.245" height="50.652" viewBox="0 0 45.245 50.652">
                            <defs>
                                <linearGradient id="asdwqdd12e" x1="0.5" y1="1" x2="0.5" gradientUnits="objectBoundingBox">
                                    <stop className="gradient-start" offset="0" stopColor="#fff"/>
                                    <stop className="gradient-end" offset="1" stopColor="#fff"/>
                                </linearGradient>
                                <clipPath id="asd212wedsaaxxx">
                                    <path d="M21.66,13.977c-1.039.649-2.125,1.222-3.187,1.836-.976.564-1.946,1.139-3.063,1.794.479.3.8.512,1.137.707q3.643,2.119,7.288,4.231,3.851,2.234,7.7,4.468c1.916,1.113,3.838,2.217,5.741,3.351a2.956,2.956,0,0,1,1.559,2.857,3.035,3.035,0,0,1-1.722,2.528c-1.117.676-2.267,1.3-3.4,1.946-3.023,1.73-6.078,3.41-9.054,5.218a3.8,3.8,0,0,1-4.322-.033c-2.787-1.662-5.617-3.252-8.429-4.872q-3.7-2.133-7.406-4.266C3.064,32.917,1.62,32.1.007,31.176c0,1.853.02,3.475-.006,5.1,0,.014,0,.028,0,.043v.046a2.713,2.713,0,0,0,1.513,2.521c1.726.968,3.439,1.96,5.153,2.949q4.577,2.639,9.151,5.285c1.853,1.066,3.715,2.116,5.573,3.173a2.557,2.557,0,0,0,1.26.363h.024a2.6,2.6,0,0,0,1.292-.378c3.121-1.8,6.25-3.586,9.371-5.386,3.394-1.957,6.768-3.949,10.181-5.872a2.978,2.978,0,0,0,1.726-2.852c-.034-2.935-.009-5.871-.015-8.807,0-.308-.048-.616-.076-.952-2.465-1.427-4.895-2.835-7.326-4.239q-4.371-2.523-8.742-5.041c-1.852-1.067-3.718-2.111-5.556-3.2a1.722,1.722,0,0,0-.887-.274,1.847,1.847,0,0,0-.982.327M21.4.374c-1.32.8-2.679,1.53-4.02,2.293C14.909,4.077,12.43,5.48,9.961,6.9c-2.777,1.6-5.523,3.25-8.325,4.8A2.871,2.871,0,0,0,.011,14.488c.021,2.976,0,5.952.006,8.928,0,.269.036.537.053.772L22.542,37.209l7.272-4.176c-.257-.188-.374-.293-.507-.368-1.857-1.06-3.723-2.1-5.573-3.173Q17.262,25.746,10.8,21.977c-.957-.557-1.9-1.146-2.872-1.667A3,3,0,0,1,7.771,15c.553-.306,1.088-.646,1.636-.962q2.879-1.661,5.761-3.314,2.988-1.709,5.985-3.4a2.828,2.828,0,0,1,2.939-.008c1.175.656,2.34,1.331,3.5,2.005q8.22,4.761,16.441,9.523c.328.19.685.332,1.169.563,0-2.015.061-3.874-.03-5.724a2.147,2.147,0,0,0-1.226-1.793c-.527-.272-1.029-.593-1.544-.888q-4.275-2.45-8.553-4.9c-3.327-1.916-6.64-3.857-9.977-5.756A2.453,2.453,0,0,0,22.658,0,2.4,2.4,0,0,0,21.4.374" fill="url(#asdwqdd12e)"/>
                                </clipPath>
                            </defs>
                            <g>
                                <g clipPath="url(#asd212wedsaaxxx)">
                                <rect id="Rectangle_8" data-name="Rectangle 8" width="45.268" height="50.652" fill="url(#asdwqdd12e)"/>
                                </g>
                            </g>
                        </svg>
                    </AppLink>
                    <div className="line"></div>
                    <aside
                        ref={menuPanelRef}
                        id="site-menu-panel"
                        className="site-menu-panel"
                        aria-hidden={!isMenuOpen}
                    >
                        <div className="site-menu-panel__inner">
                            <nav className="site-menu-panel__section" aria-label={content.ariaLabel}>
                                <p ref={setMenuRevealItem(0)} className="site-menu-panel__eyebrow">{content.menuLabel}</p>
                                <ul className="site-menu-panel__nav">
                                    {menuLinks.map(({ text, to, target }, index) => (
                                        <li ref={setMenuRevealItem(index + 1)} key={text}>
                                            <AppLink to={to} target={target} onClick={handleMenuLinkClick} tabIndex={isMenuOpen ? undefined : -1}>
                                                {text}
                                            </AppLink>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                            <div className="site-menu-panel__section site-menu-panel__section--social">
                                <p ref={setMenuRevealItem(menuLinks.length + 1)} className="site-menu-panel__eyebrow">{content.socialLabel}</p>
                                <ul className="site-menu-panel__list">
                                    {MENU_SOCIAL_LINKS.map(({ text, to, target }, index) => (
                                        <li ref={setMenuRevealItem(menuLinks.length + 2 + index)} key={text}>
                                            <AppLink to={to} target={target} onClick={handleMenuLinkClick} tabIndex={isMenuOpen ? undefined : -1}>
                                                {text}
                                            </AppLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="site-menu-panel__section site-menu-panel__section--phone">
                                <p ref={setMenuRevealItem(menuLinks.length + MENU_SOCIAL_LINKS.length + 2)} className="site-menu-panel__eyebrow">{content.phoneLabel}</p>
                                <a
                                    ref={setMenuRevealItem(menuLinks.length + MENU_SOCIAL_LINKS.length + 3)}
                                    href={MENU_CONTACT_INFO.phoneUrl}
                                    tabIndex={isMenuOpen ? undefined : -1}
                                >
                                    {MENU_CONTACT_INFO.phone}
                                </a>
                            </div>
                        </div>
                    </aside>
                    <div className="menu-btns">
							<Button
								ref={languageButtonRef}
								className="language-toggle"
								data-router={false}
								to={content.languagePath}
								text={content.languageLabel}
							/>
                        <Button
                            ref={menuButtonRef}
                            textRef={menuButtonTextRef}
                            className="menu-toggle"
                            text={menuButtonLabel}
                            onClick={toggleMenu}
                            aria-controls="site-menu-panel"
                            aria-expanded={isMenuOpen}
                            after={
                                <><svg ref={menuIconRef} className="menu-toggle__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><g transform="translate(-1783 -46)"><rect width="6" height="6" transform="translate(1783 58)" fill="currentColor"/><rect width="6" height="6" transform="translate(1795 46)" fill="currentColor"/><rect width="6" height="6" transform="translate(1783 46)" fill="currentColor"/><rect width="6" height="6" transform="translate(1795 58)" fill="currentColor"/></g></svg></>
                            }
                        />
                    </div>
                </div>
            </div>
        </header>,
        mountNode
    );
});

export default Header;
